/**
 * A resumed draft must not carry a meeting date that has already passed.
 *
 * Drafts outlive the slot they booked: a client who picks Friday 10:00 and comes
 * back the following week resumes a draft whose date is in the past. Legacy
 * could not hit this — it had no drafts — but it did reject a past date outright
 * (`currentFieldErrors`: `dateIsPast`).
 *
 * v2 relied on the picker's `disablePast` plus the lead-time floor, and the
 * floor is skipped entirely when the BE returns no timezone, so a stale date
 * could survive all the way to the order. Product decision (2026-08-25): the
 * date is CLEARED rather than flagged, so the field reads as empty-and-required
 * and the client picks a new slot.
 *
 * Clearing only the date, not the whole meeting, is deliberate: the contact
 * name, phone, email and pickup address are still correct, and making the
 * client retype them would be a worse resume than the one this fixes.
 */

import moment from 'moment-timezone'

import type { MeetingDTO } from 'models/purchaseFlowRevamp'

/** Modes that carry a date at all — a third-party contact never has one. */
const DATED_MODES = ['ON_SITE', 'KEY_PICKUP'] as const

/**
 * Strips a meeting date that is already in the past, leaving every other field.
 *
 * @param meeting the resumed meeting, if any
 * @param now injectable clock, so the rule is testable without freezing time
 *
 * @example
 * clearStaleMeetingDate({ mode: 'ON_SITE', onSite: { date: lastWeek, contactName: 'Ada' } })
 * // → { mode: 'ON_SITE', onSite: { contactName: 'Ada' } }   // date dropped, name kept
 */
export const clearStaleMeetingDate = (
  meeting: MeetingDTO | undefined,
  now: moment.Moment = moment()
): MeetingDTO | undefined => {
  if (!meeting) return meeting

  for (const mode of DATED_MODES) {
    if (meeting.mode !== mode) continue

    const branch = mode === 'ON_SITE' ? meeting.onSite : meeting.keyPickup
    if (!branch?.date) return meeting
    if (!moment(branch.date).isBefore(now)) return meeting

    const { date, ...kept } = branch
    return mode === 'ON_SITE'
      ? { ...meeting, onSite: kept }
      : { ...meeting, keyPickup: kept }
  }

  return meeting
}
